import * as React from "react";

import { Entry } from "../models/entries";
import { TrophyJSON } from "../models/trophies";
import { QueueSong } from "../Toolbar/QueueSong";
import { User } from "../models/users";
import { api } from "../api";
import { showToast } from "../Layout/ToastDisplay";
import { RLink } from "../DesignSystem/RLink";
import { HeartButton } from "../DesignSystem/HeartButton";
import { Tooltip } from "../DesignSystem/Tooltip";
import {
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import {
  ArrowDownTrayIcon,
  PlayCircleIcon,
  StarIcon,
} from "@heroicons/react/24/solid";
import { TestIds } from "../testIds";

function getScoreColor(score: number | undefined): string {
  if (!score) return "rgb(150, 150, 150)";
  if (score < 2.5) return "rgb(53, 25, 109)";
  if (score < 3) return "rgb(122, 51, 255)";
  if (score < 3.5) return "rgb(64, 67, 231)";
  if (score < 4.0) return "rgb(23, 224, 228)";
  if (score < 4.2) return "rgb(33, 218, 73)";
  if (score < 4.4) return "rgb(179, 238, 16)";
  return "rgb(240, 206, 13)";
}

export type EntryStatsProps = {
  entry: Entry;
  user: User | undefined;
  showCollabButton?: boolean;
  faved?: boolean;
  commed?: boolean;
  trophies?: TrophyJSON[];
  variant?: "default" | "hero";
};

export type EntryStatsState = {
  currentUserLikes: boolean;
  heartsDelta: number;
};

const SpanThing = (props: { children: React.ReactNode }) => {
  return (
    <span className="inline-flex items-center gap-2 text-current">
      {props.children}
    </span>
  );
};

export default class EntryStats extends React.Component<
  EntryStatsProps,
  EntryStatsState
> {
  state: EntryStatsState = {
    currentUserLikes: false,
    heartsDelta: 0,
  };

  componentDidMount() {
    this.setState({
      currentUserLikes: this.props.faved || false,
    });
  }

  componentWillReceiveProps(newProps: EntryStatsProps): void {
    // Only reset if the entry changed (different entry) or if faved prop actually changed from server
    if (newProps.entry.id !== this.props.entry.id) {
      this.setState({
        currentUserLikes: newProps.faved || false,
        heartsDelta: 0,
      });
    }
  }

  async favorite(): Promise<void> {
    const { user, entry } = this.props;

    if (!user) {
      showToast({
        type: "error",
        msg: "Please log in before favoriting a track.",
      });

      return;
    }

    const isCurrentlyLiked = this.state.currentUserLikes;

    if (isCurrentlyLiked) {
      const result = await api.delete<"/api/entry/:id/favorite">(
        `/api/entry/${entry.id}/favorite`
      );

      if (api.isToast(result)) {
        return;
      }

      this.setState((prevState) => ({
        heartsDelta: prevState.heartsDelta - 1,
        currentUserLikes: false,
      }));
    } else {
      const result = await api.post<"/api/entry/:id/favorite">(
        `/api/entry/${entry.id}/favorite`
      );

      if (api.isToast(result)) {
        return;
      }

      this.setState((prevState) => ({
        heartsDelta: prevState.heartsDelta + 1,
        currentUserLikes: true,
      }));
    }
  }

  async download(): Promise<void> {
    const { entry } = this.props;

    if (!entry.uploaded_url) return;

    // Track the download
    await api.post<"/api/entry/:id/download">(
      `/api/entry/${entry.id}/download`
    );

    // With CORS configured, we can fetch and download properly
    const response = await fetch(entry.uploaded_url);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `${entry.title}.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  }

  render(): JSX.Element {
    const { entry, trophies, variant = "default" } = this.props;

    const liked = this.state.currentUserLikes;
    const favoritesCount = Math.max(
      0,
      (entry.hearts || 0) + this.state.heartsDelta
    );

    return variant === "hero"
      ? this.renderHeroLayout({
          entry,
          liked,
          favoritesCount,
          trophies,
        })
      : this.renderDefaultLayout({
          entry,
          liked,
          favoritesCount,
          trophies,
        });
  }

  private renderDefaultLayout(params: {
    entry: Entry;
    liked: boolean;
    favoritesCount: number;
    trophies?: TrophyJSON[];
  }): JSX.Element {
    const { entry, liked, favoritesCount, trophies } = params;

    return (
      <div className="flex flex-wrap items-center gap-1 text-[14px] text-gray-500 dark:text-slate-400">
        <SpanThing>
          {this.renderLikeStat({
            liked,
            favoritesCount,
            variant: "default",
          })}
        </SpanThing>

        {entry.type === "song" && (
          <SpanThing>
            <span className="inline-flex items-center gap-2">
              <PlayCircleIcon
                className="h-4 w-4 text-slate-500 dark:text-white/70"
                aria-hidden="true"
              />
              {entry.plays || 0}
            </span>
          </SpanThing>
        )}

        <SpanThing>
          <span className="inline-flex items-center gap-2">
            <DocumentTextIcon
              className="h-4 w-4 text-slate-500 dark:text-white/70"
              aria-hidden="true"
            />
            {entry.reviews || 0}
          </span>
        </SpanThing>

        <SpanThing>
          <span className="inline-flex items-center gap-2">
            <ChatBubbleLeftRightIcon
              className="h-4 w-4 text-slate-500 dark:text-white/70"
              aria-hidden="true"
            />
            {entry.comments || 0}
          </span>
        </SpanThing>

        {(entry.downloadable || this.props.user?.admin) &&
          entry.type === "song" &&
          (() => {
            const isAdminOnlyDownload =
              !entry.downloadable && this.props.user?.admin;
            const adminDownloadTooltip =
              "The author of this entry would prefer it not be downloaded. Please only use this for streaming and sync purposes. Thanks!";

            const downloadButton = (
              <button
                type="button"
                onClick={() => this.download()}
                className={
                  isAdminOnlyDownload
                    ? "inline-flex cursor-pointer items-center gap-2 text-violet-600 transition hover:text-violet-800 dark:text-violet-400 dark:hover:text-violet-300"
                    : "inline-flex cursor-pointer items-center gap-2 text-slate-600 transition hover:text-slate-800 dark:text-white/80 dark:hover:text-white"
                }
              >
                <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
                Download
              </button>
            );

            return (
              <SpanThing>
                {isAdminOnlyDownload ? (
                  <Tooltip
                    element={downloadButton}
                    tooltip={adminDownloadTooltip}
                  />
                ) : (
                  downloadButton
                )}
                <span>{entry.downloads || 0}</span>
              </SpanThing>
            );
          })()}

        {trophies && trophies.length > 0 && (
          <SpanThing>
            {trophies.map((trophy) => (
              <img
                key={trophy.id}
                className="inline-block h-[15px] w-[15px] px-[2px]"
                src={trophy.trophy_url}
              ></img>
            ))}
          </SpanThing>
        )}
        {entry.type === "song" && (
          <SpanThing>
            <QueueSong dark entry={entry} />
          </SpanThing>
        )}

        {this.props.showCollabButton && (
          <SpanThing>
            <RLink
              to={`/entry/${entry.id}/edit`}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:border-chips-gold hover:text-chips-gold dark:border-white/20 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-chips-gold dark:hover:text-chips-gold"
            >
              <i className="fa fa-pencil" aria-hidden="true"></i>
              Edit
            </RLink>
          </SpanThing>
        )}
      </div>
    );
  }

  private renderHeroLayout(params: {
    entry: Entry;
    liked: boolean;
    favoritesCount: number;
    trophies?: TrophyJSON[];
  }): JSX.Element {
    const { entry, liked, favoritesCount, trophies } = params;

    const pillBase =
      "inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2 py-1 text-sm font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400";
    const pillInteractive = `${pillBase} cursor-pointer transition hover:bg-slate-200 dark:hover:bg-slate-700`;
    const pillAdminOnly =
      "inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-violet-100 px-2 py-1 text-sm font-medium text-violet-700 transition hover:bg-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:hover:bg-violet-900/50";

    const adminDownloadTooltip =
      "The author of this entry would prefer it not be downloaded. Please only use this for streaming and sync purposes. Thanks!";

    const isAdminOnlyDownload =
      !entry.downloadable && this.props.user?.admin;
    const canDownload = entry.downloadable || this.props.user?.admin;

    const scoreValue =
      !entry.hidden && entry.overall_score
        ? entry.overall_score.toFixed(2)
        : "?.??";
    const scoreColor = entry.hidden
      ? "rgb(150, 150, 150)"
      : getScoreColor(entry.overall_score);

    const statText =
      "inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 dark:text-slate-400";

    return (
      <div className="flex flex-wrap items-center gap-2">
        {/* Score (no background) */}
        <span className={statText} style={{ color: scoreColor }}>
          <StarIcon className="h-4 w-4" aria-hidden="true" />
          {scoreValue}
        </span>

        {/* Heart */}
        {this.renderLikeStat({ liked, favoritesCount, variant: "hero" })}

        {/* Downloads */}
        {entry.type === "song" &&
          canDownload &&
          (() => {
            const downloadButton = (
              <button
                type="button"
                onClick={() => this.download()}
                className={
                  isAdminOnlyDownload ? pillAdminOnly : pillInteractive
                }
              >
                <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
                <span>{entry.downloads || 0}</span>
              </button>
            );

            return isAdminOnlyDownload ? (
              <Tooltip element={downloadButton} tooltip={adminDownloadTooltip} />
            ) : (
              downloadButton
            );
          })()}

        {/* Plays (no background) */}
        {entry.type === "song" && (
          <span className={statText}>
            <PlayCircleIcon className="h-4 w-4" aria-hidden="true" />
            {entry.plays || 0}
          </span>
        )}

        {/* Trophies */}
        {trophies && trophies.length > 0 && (
          <span className={pillBase}>
            {trophies.map((trophy) => (
              <img
                key={trophy.id}
                className="h-4 w-4"
                src={trophy.trophy_url}
              ></img>
            ))}
          </span>
        )}

        {/* Edit button */}
        {this.props.showCollabButton && (
          <RLink to={`/entry/${entry.id}/edit`} className={pillInteractive}>
            <i className="fa fa-pencil text-xs" aria-hidden="true"></i>
            Edit
          </RLink>
        )}
      </div>
    );
  }

  private renderLikeStat(params: {
    liked: boolean;
    favoritesCount: number;
    variant: "default" | "hero";
  }): JSX.Element {
    const { liked, favoritesCount } = params;

    return (
      <HeartButton
        liked={liked}
        count={favoritesCount}
        onClick={() => this.favorite()}
        testId={TestIds.EntryLikeButton}
        countTestId={TestIds.EntryLikeCount}
        entryId={this.props.entry.id}
      />
    );
  }

}
